import { type Explainer, type ExplainerSummary } from "../types";

export const arraySections = ["supportCharges"];
export const objectSections = ["serviceCharges", "discounts", "savings", "otherCharges"];
export const numberSections = ["total", "tax", "credits", "refunds"];

export const LABELS: Record<string, string> = {
  usage: "Usage",
  savingsPlanUsage: "Savings Plans covered usage",
  flexsaveCoveredUsage: "Flexsave covered usage",
  savingsPlanUpfrontFee: "Savings Plan upfront fee",
  edp: "Enterprise Discount Program discount",
  spp: "Solution Provider Program discount",
  privateRate: "Private Rate Card discount",
  bundled: "Bundled discount",
  planNegation: "Savings plans negation",
  flexsave: "Flexsave Savings",
  reservationAppliedUsage: "Reservation applied usage",
  ocb: "Other out-of-cycle charges",
  savingsPlanRecurringFee: "Savings Plans recurring fee",
  reservationRecurringFee: "Recurring reservation fee",
  credits: "Credits",
  refunds: "Refunds",
  tax: "Tax",
  total: "Total",
  accountId: "Account ID",
  supportLevel: "Support Level",
  baseCost: "Base Cost",
  description: "Description",
  servicesTotal: "Total service charges",
  supportChargesTotal: "Total support charges",
  discountTotal: "Total discounts",
  savingsTotal: "Total savings",
  otherChargesTotal: "Total other charges",
};

export const getCurrencyPattern = (currency: string): string => {
  switch (currency) {
    case "USD":
      return "$#,##0.00";
    case "EUR":
      return "€#,##0.00";
    case "GBP":
      return "£#,##0.00";
    case "ILS":
      return "₪#,##0.00";
    case "AUD":
      return "A$#,##0.00";
    case "CAD":
      return "C$#,##0.00";
    case "DKK":
      return "kr#,##0.00";
    case "NOK":
      return "kr#,##0.00";
    case "SEK":
      return "kr#,##0.00";
    case "BRL":
      return "R$#,##0.00";
    case "SGD":
      return "S$#,##0.00";
    case "INR":
      return "₹#,##0.00";
    case "JPY":
      return "¥#,##0.00";
    case "CNY":
      return "¥#,##0.00";
    case "CHF":
      return "CHF#,##0.00";
    case "COP":
      return "$#,##0.00";
    default:
      return "$#,##0.00";
  }
};

export const getSectionTotalKey = (section: keyof ExplainerSummary): string | undefined => {
  switch (section) {
    case "serviceCharges":
      return "servicesTotal";
    case "discounts":
      return "discountTotal";
    case "savings":
      return "savingsTotal";
    case "otherCharges":
      return "otherChargesTotal";
    case "supportCharges":
      return "supportChargesTotal";
    default:
      return undefined;
  }
};

export const getSectionDefs = (explainer: Explainer) => [
  {
    key: "serviceCharges",
    label: "Service Charges",
    headers: Object.keys(explainer.summary.aws.serviceCharges),
  },
  {
    key: "supportCharges",
    label: "Support Charges",
    headers: ["accountId", "supportLevel", "total", "baseCost", "description"],
  },
  {
    key: "credits",
    label: "Credits",
    headers: ["credits"],
  },
  {
    key: "discounts",
    label: "Discounts",
    headers: Object.keys(explainer.summary.aws.discounts),
  },
  {
    key: "savings",
    label: "Savings",
    headers: Object.keys(explainer.summary.aws.savings),
  },
  {
    key: "refunds",
    label: "Refunds",
    headers: ["refunds"],
  },
  {
    key: "otherCharges",
    label: "Other Charges",
    headers: Object.keys(explainer.summary.aws.otherCharges),
  },
  {
    key: "tax",
    label: "Tax",
    headers: ["tax"],
  },
  {
    key: "total",
    label: "Total",
    headers: ["total"],
  },
];

export const emptyLine: gapi.client.sheets.RowData = {
  values: [{ userEnteredValue: { stringValue: "" } }, { userEnteredValue: { stringValue: "" } }],
};

export const getSectionTotalLabel = (section: keyof ExplainerSummary): string | undefined => {
  const key = getSectionTotalKey(section);
  return key ? LABELS[key] : undefined;
};

export const getCurrencyCellFormat = (currency: string): gapi.client.sheets.CellFormat => ({
  numberFormat: {
    type: "CURRENCY",
    pattern: getCurrencyPattern(currency),
  },
});

export const buildSectionLabelRow = (label: string): gapi.client.sheets.RowData => ({
  values: [
    {
      userEnteredValue: { stringValue: label },
      userEnteredFormat: { textFormat: { bold: true } },
    },
  ],
});

export const createCell = (
  value: string | number | unknown,
  numberFormat?: gapi.client.sheets.CellFormat
): gapi.client.sheets.CellData =>
  typeof value === "number"
    ? {
        userEnteredValue: { numberValue: value },
        ...(numberFormat ? { userEnteredFormat: numberFormat } : {}),
      }
    : {
        userEnteredValue: { stringValue: value !== undefined && value !== null ? String(value) : "" },
      };

export const buildArraySectionRows = (
  sectionData: Record<string, unknown>[] | undefined,
  headers: string[],
  currencyCellFormat: gapi.client.sheets.CellFormat
): gapi.client.sheets.RowData[] => {
  if (!sectionData) return [];
  return sectionData.map((row) => ({
    values: headers.map((header) => createCell(row[header], currencyCellFormat)),
  }));
};

export const buildObjectSectionRows = (
  sectionData: Record<string, unknown> | undefined,
  currencyCellFormat?: gapi.client.sheets.CellFormat
): gapi.client.sheets.RowData[] => {
  if (!sectionData) return [];
  return Object.keys(sectionData).reduce<gapi.client.sheets.RowData[]>((acc, key) => {
    const value = sectionData[key];
    if (value === undefined || value === null) return acc;

    acc.push({
      values: [createCell(LABELS[key]), createCell(value, currencyCellFormat)],
    });

    return acc;
  }, []);
};

export const buildNumberSectionRows = (
  value: number | undefined,
  label: string,
  currencyCellFormat: gapi.client.sheets.CellFormat
): gapi.client.sheets.RowData[] => {
  if (!value) return [];
  return [
    {
      values: [createCell(label), createCell(value, currencyCellFormat)],
    },
  ];
};
